import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PaginationState,
  PaginationInfo,
  PAGE_SIZE_OPTIONS,
  calculatePaginationInfo,
  createPaginationHandlers,
  getPaginationSummary
} from '../../utils/pagination-utils';

interface PaginationControlsProps {
  /** Current pagination state */
  paginationState: PaginationState;
  /** Callback when pagination state changes */
  onPaginationChange: (newState: PaginationState) => void;
  /** Additional CSS classes */
  className?: string;
  /** Show page size selector */
  showPageSize?: boolean;
  /** Show page numbers */
  showPageNumbers?: boolean;
  /** Show navigation arrows */
  showNavigation?: boolean;
  /** Show summary text */
  showSummary?: boolean;
  /** Compact layout for smaller spaces */
  compact?: boolean;
}

/**
 * PaginationControls - A comprehensive pagination component
 * Features:
 * - Page navigation (first, previous, next, last)
 * - Page size selector (10/20/50/100/All)
 * - Page number buttons
 * - Results summary
 * - Responsive design
 * - Color Kit styling
 */
const PaginationControls: React.FC<PaginationControlsProps> = ({
  paginationState,
  onPaginationChange,
  className = '',
  showPageSize = true,
  showPageNumbers = true,
  showNavigation = true,
  showSummary = true,
  compact = false
}) => {
  const paginationInfo: PaginationInfo = calculatePaginationInfo(paginationState);
  const handlers = createPaginationHandlers(paginationState, onPaginationChange);
  const summary = getPaginationSummary(paginationState);

  // Don't show pagination if there's only one page and no items
  if (paginationState.totalItems === 0) {
    return null;
  }

  // Don't show pagination if there's only one page and page size is "Show all"
  if (paginationInfo.totalPages <= 1 && paginationState.pageSize === -1) {
    return showSummary ? (
      <div className={`flex items-center justify-center py-2 ${className}`}>
        <span className="text-sm text-muted-foreground">{summary}</span>
      </div>
    ) : null;
  }

  return (
    <div className={`flex items-center justify-between gap-4 py-4 ${compact ? 'py-2' : 'py-4'} ${className}`}>
      {/* Left side - Summary and Page Size */}
      <div className="flex items-center gap-4">
        {showSummary && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {summary}
          </span>
        )}
        
        {showPageSize && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {compact ? 'Per page:' : 'Results per page:'}
            </span>
            <Select
              value={paginationState.pageSize.toString()}
              onValueChange={(value) => handlers.changePageSize(parseInt(value))}
            >
              <SelectTrigger className="w-auto min-w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Right side - Navigation */}
      {(showNavigation || showPageNumbers) && paginationInfo.totalPages > 1 && (
        <div className="flex items-center gap-2">
          {showNavigation && (
            <>
              {/* First page */}
              <Button
                variant="outline"
                size={compact ? "sm" : "default"}
                onClick={handlers.goToFirstPage}
                disabled={!paginationInfo.hasPreviousPage}
                className="h-8 w-8 p-0"
                aria-label="Go to first page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>

              {/* Previous page */}
              <Button
                variant="outline"
                size={compact ? "sm" : "default"}
                onClick={handlers.goToPreviousPage}
                disabled={!paginationInfo.hasPreviousPage}
                className="h-8 w-8 p-0"
                aria-label="Go to previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          )}

          {/* Page numbers */}
          {showPageNumbers && (
            <div className="flex items-center gap-1">
              {paginationInfo.visiblePages.map((pageNumber, index) => {
                const isCurrentPage = pageNumber === paginationState.currentPage;
                
                // Show ellipsis for gaps
                const showEllipsisBefore = index === 0 && pageNumber > 1;
                const showEllipsisAfter = index === paginationInfo.visiblePages.length - 1 && pageNumber < paginationInfo.totalPages;
                
                return (
                  <React.Fragment key={pageNumber}>
                    {showEllipsisBefore && (
                      <span className="px-2 py-1 text-sm text-muted-foreground">...</span>
                    )}
                    
                    <Button
                      variant={isCurrentPage ? "default" : "outline"}
                      size={compact ? "sm" : "default"}
                      onClick={() => handlers.goToPage(pageNumber)}
                      className={`h-8 min-w-[32px] ${isCurrentPage ? 'bg-primary text-primary-foreground' : ''}`}
                      aria-label={`Go to page ${pageNumber}`}
                      aria-current={isCurrentPage ? 'page' : undefined}
                    >
                      {pageNumber}
                    </Button>
                    
                    {showEllipsisAfter && (
                      <span className="px-2 py-1 text-sm text-muted-foreground">...</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {showNavigation && (
            <>
              {/* Next page */}
              <Button
                variant="outline"
                size={compact ? "sm" : "default"}
                onClick={handlers.goToNextPage}
                disabled={!paginationInfo.hasNextPage}
                className="h-8 w-8 p-0"
                aria-label="Go to next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Last page */}
              <Button
                variant="outline"
                size={compact ? "sm" : "default"}
                onClick={handlers.goToLastPage}
                disabled={!paginationInfo.hasNextPage}
                className="h-8 w-8 p-0"
                aria-label="Go to last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PaginationControls;